import {Letter} from "@game/map/letter";
import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";

interface IHand {
    letters: string[]
    player: string
    shovels: number
}
export class Hand {
    private readonly _events = new EventHandler<GameEvent>();
    private readonly _held: Letter[] = [];
    private readonly _placed: Letter[] = [];
    private _shovels: number;

    private _dragging?: Letter;

    constructor(private props: IHand) {
        this._held = props.letters.map(letter => new Letter({letter, player: props.player}));
        this._shovels = props.shovels;
    }

    get dragging() {
        return this._dragging;
    }

    get events() {
        return this._events;
    }

    get held() {
        return this._held;
    }

    get letters() {
        return [...this._held, ...this._placed, ...this._dragging ? [this._dragging] : []];
    }
    
    get placed() {
        return this._placed;
    }
    
    get player() {
        return this.props.player;
    }

    get shovels() {
        return this._shovels;
    }
    dragLetter(letter: Letter) {
        const index = this._held.indexOf(letter);

        if (index >= 0) {
            this._dragging = this._held.splice(index, 1)[0];
            this.events.update(new GameEvent("DragLetter"));
            return true;
        }
        return false;
    }

    dragPlacedLetter(letter: Letter) {
        const index = this._placed.indexOf(letter);
        
        if (index >= 0 && ! this._dragging) {
            this._dragging = this._placed.splice(index, 1)[0];
            this.events.update(new GameEvent("DragPlacedLetter"));
            return true;
        }
        return false;
    }

    dropLetter(insert: number) {
        if (this._dragging) {
            if (insert >= 0) {
                this._held.splice(insert, 0, this._dragging);
            } else {
                this._held.push(this._dragging);
            }
            this._dragging = undefined;
            this.events.update(new GameEvent("DroppedLetter"));
        }
    }

    placeDraggedLetter() {
        if (this._dragging) {
            this._placed.push(this._dragging);
            this._dragging = undefined;
        }
    }
    
    resetPlacedLetters() {
        this.dropLetter(-1);
        this._held.push(...this._placed.splice(0, this.placed.length));
        this.events.update(new GameEvent("ResetPlacedLetters"))
    }

    updateLetters(letters: string[], clearPlaced = false) {
        // On successful word submission the server has already committed placed letters,
        // so _placed must be cleared before reconciling with the server hand.
        if (clearPlaced) {
            this._placed.splice(0, this._placed.length);
        }
        // The server hand still includes in-flight placed and dragging letters
        // (it doesn't know about client-side placements). Remove them before updating _held.
        const adjusted = [...letters];
        for (const placed of this._placed) {
            const idx = adjusted.indexOf(placed.letter);
            if (idx >= 0) adjusted.splice(idx, 1);
        }
        if (this._dragging) {
            const idx = adjusted.indexOf(this._dragging.letter);
            if (idx >= 0) adjusted.splice(idx, 1);
        }
        if (adjusted.length !== this._held.length) {
            this._held.splice(0, this._held.length,
                ...adjusted.map(letter => new Letter({letter, player: this.player})));
            this.events.update(new GameEvent("Updated"));
        }
    }

    updateShovels(shovels: number) {
        this._shovels = shovels;
    }
}